
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Clock, ThumbsUp } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface MemoryCard {
  id: number;
  value: number;
  flipped: boolean;
  matched: boolean;
}

const MemoryMatch = () => {
  const [cards, setCards] = useState<MemoryCard[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [matchedPairs, setMatchedPairs] = useState<number>(0);
  const [moves, setMoves] = useState<number>(0);
  const [gameOver, setGameOver] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(0);
  const { toast } = useToast();
  
  // Initialize game
  useEffect(() => {
    initializeGame();
  }, []);
  
  // Timer
  useEffect(() => {
    let interval: number | undefined;
    
    if (!gameOver && cards.length > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000) as unknown as number;
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [gameOver, cards.length]);
  
  // Check for game completion
  useEffect(() => {
    if (matchedPairs === 6 && cards.length > 0) {
      setGameOver(true);
      toast({
        title: "Congratulations!",
        description: `You completed the game in ${moves} moves and ${formatTime(timer)}`,
      });
    }
  }, [matchedPairs, cards.length, moves, timer, toast]);
  
  const initializeGame = () => {
    // Create card pairs (1-6, each appears twice)
    const cardValues = [...Array(6).keys()].map(i => i + 1);
    const cardPairs = [...cardValues, ...cardValues];
    
    // Shuffle cards
    const shuffledCards = cardPairs
      .sort(() => Math.random() - 0.5)
      .map((value, index) => ({
        id: index,
        value,
        flipped: false,
        matched: false
      }));
    
    setCards(shuffledCards);
    setFlippedCards([]);
    setMatchedPairs(0);
    setMoves(0);
    setTimer(0);
    setGameOver(false);
  };
  
  const handleCardClick = (id: number) => {
    // Ignore clicks if game is over or card is already flipped/matched
    if (gameOver || flippedCards.length >= 2) return;
    
    const clickedCard = cards.find(card => card.id === id);
    if (!clickedCard || clickedCard.flipped || clickedCard.matched) return;
    
    // Flip the card
    const updatedCards = cards.map(card => 
      card.id === id ? { ...card, flipped: true } : card
    );
    
    const updatedFlippedCards = [...flippedCards, id];
    
    setCards(updatedCards);
    setFlippedCards(updatedFlippedCards);
    
    // If this is the second card flipped, check for a match
    if (updatedFlippedCards.length === 2) {
      setMoves(prev => prev + 1);
      
      const [firstId, secondId] = updatedFlippedCards;
      const firstCard = updatedCards.find(card => card.id === firstId);
      const secondCard = updatedCards.find(card => card.id === secondId);
      
      if (firstCard?.value === secondCard?.value) {
        // Match found
        setTimeout(() => {
          const matchedCards = updatedCards.map(card => 
            card.id === firstId || card.id === secondId 
              ? { ...card, matched: true } 
              : card
          );
          
          setCards(matchedCards);
          setFlippedCards([]);
          setMatchedPairs(prev => prev + 1);
        }, 500);
      } else {
        // No match
        setTimeout(() => {
          const resetCards = updatedCards.map(card => 
            card.id === firstId || card.id === secondId 
              ? { ...card, flipped: false } 
              : card
          );
          
          setCards(resetCards);
          setFlippedCards([]);
        }, 1000);
      }
    }
  };
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  
  const cardColor = (value: number) => {
    const colors = [
      "bg-red-500", "bg-blue-500", "bg-green-500", 
      "bg-yellow-500", "bg-purple-500", "bg-pink-500"
    ];
    return colors[value - 1];
  };
  
  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-2">
          <Badge variant="outline" className="flex items-center gap-1">
            <Clock className="h-3 w-3" /> {formatTime(timer)}
          </Badge>
          <Badge variant="outline" className="flex items-center gap-1">
            <ThumbsUp className="h-3 w-3" /> {moves} moves
          </Badge>
        </div>
        <Button size="sm" variant="outline" onClick={initializeGame}>
          New Game
        </Button>
      </div>
      
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        {cards.map(card => (
          <div 
            key={card.id}
            onClick={() => handleCardClick(card.id)}
            className={`aspect-square rounded-md cursor-pointer transition-all transform ${
              card.flipped || card.matched 
                ? `${cardColor(card.value)} text-white font-bold flex items-center justify-center`
                : "bg-slate-700 hover:bg-slate-600"
            } ${card.matched ? "animate-pulse" : ""}`}
          >
            {(card.flipped || card.matched) && (
              <span className="text-xl">{card.value}</span>
            )}
          </div>
        ))}
      </div>
      
      {gameOver && (
        <div className="mt-4 text-center">
          <p className="text-green-600 font-bold">Game Complete!</p>
          <p className="text-sm">
            You found all pairs in {moves} moves and {formatTime(timer)}
          </p>
        </div>
      )}
    </div>
  );
};

export default MemoryMatch;
